import { ConvoAIWebSocketAPI, type ConvoAIWebSocketServerMessage } from '../../api/AudioAPI';
import { processWEBMAudioToTextWithPCM16 } from '../processAudioToText';

import type { WebSpeechToTextAPIAction } from './WebSpeechToTextModels';

export class WebSpeechToTextManager {
	private audioChunks: Blob[] = [];
	private lastProcessedPosition: number = 0;
	private convoAIWebSocketAPI: ConvoAIWebSocketAPI | null = null;
	private mediaStream: MediaStream | null = null;
	private mediaRecorder: MediaRecorder | null = null;
	private previousAudioString: string | null = null;

	constructor(
		private readonly onTranscription: (action: WebSpeechToTextAPIAction) => void,
		private readonly cloudID: string,
		private readonly sampleRate = 24000,
		private readonly audioBitsPerSecond = 16000,
		private readonly timeSlice = 1000,
		private readonly product = 'confluence',
		private readonly experience = 'ai-mate',
		private readonly intent = 'transcription',
	) {}

	public async startRecording() {
		this.onTranscription({ type: 'SET_INITIALIZING' });
		await this.connectWebSocketConvoAIAPI();

		this.mediaStream = await navigator.mediaDevices.getUserMedia({
			audio: {
				sampleRate: this.sampleRate,
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
		});

		this.resetInMemoryState();

		// eslint-disable-next-line compat/compat
		this.mediaRecorder = new MediaRecorder(this.mediaStream, {
			audioBitsPerSecond: this.audioBitsPerSecond,
			mimeType: 'audio/webm',
		});

		this.onTranscription({ type: 'SET_RECORDING', payload: { transcription: '' } });

		this.mediaRecorder.start(this.timeSlice);

		this.mediaRecorder.addEventListener('dataavailable', this.onMediaRecorderDataAvailable);
		this.mediaRecorder.addEventListener('stop', this.onMediaRecorderStop);
		this.mediaRecorder.addEventListener('error', this.onMediaRecorderError);
	}

	public stopRecording() {
		this.onTranscription({ type: 'SET_STOPPED' });
		this.mediaRecorder?.stop();
	}

	private async connectWebSocketConvoAIAPI() {
		try {
			this.convoAIWebSocketAPI = await ConvoAIWebSocketAPI.create(
				this.cloudID,
				this.product,
				this.experience,
				this.intent,
				this.onMessage,
			);
			this.convoAIWebSocketAPI.updateTranscriptionConfig();
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	}

	private onMessage = (event: ConvoAIWebSocketServerMessage) => {
		this.onTranscription({
			type: 'UPDATE_TRANSCRIPTION',
			payload: { nextTranscription: event.message.content },
		});
	};

	private async disconnect() {
		this.convoAIWebSocketAPI?.disconnect();
	}

	private onMediaRecorderStop = () => {
		this.mediaStream?.getTracks().forEach((track) => track.stop());
		this.disconnect();
	};

	private onMediaRecorderError = (event: MediaRecorderEventMap['error']) => {
		this.onTranscription({ type: 'SET_ERROR', payload: { error: event.message } });
	};

	private onMediaRecorderDataAvailable = async (event: MediaRecorderEventMap['dataavailable']) => {
		this.audioChunks.push(event.data);

		try {
			const { audioString, newAudioEndPosition } = await processWEBMAudioToTextWithPCM16(
				this.audioChunks,
				this.lastProcessedPosition,
				this.sampleRate,
			);
			this.lastProcessedPosition = newAudioEndPosition;

			if (audioString) {
				this.previousAudioString = audioString;
				this.convoAIWebSocketAPI?.sendAudio(audioString);
			} else if (audioString === null && this.previousAudioString !== null) {
				this.convoAIWebSocketAPI?.commitAudio();
				this.previousAudioString = null;
			}
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	};

	private resetInMemoryState() {
		this.audioChunks = [];
		this.lastProcessedPosition = 0;
	}
}
